import { useRef, useState } from "react";
import { Camera, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { Button } from "@bookdental/ui";
import { toast } from "sonner";
import { MAX_AVATAR_DATA_URL_LENGTH } from "@bookdental/schemas";
import { cn } from "@/lib/cn";

type Props = {
  value: string | null | undefined;
  onChange: (dataUrl: string | null) => void;
  label?: string;
  className?: string;
};

/** Upload de logo em base64. Usado em RadiologyProfile e LabProfile. */
export function LogoUploader({ value, onChange, label = "Logo", className }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem");
      return;
    }
    if (file.size > 500_000) {
      toast.error("Imagem muito grande (max ~500KB)");
      return;
    }
    setLoading(true);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setLoading(false);
      if (dataUrl.length > MAX_AVATAR_DATA_URL_LENGTH) {
        toast.error("Imagem muito grande apos codificacao");
        return;
      }
      onChange(dataUrl);
    };
    reader.onerror = () => {
      setLoading(false);
      toast.error("Falha ao ler o arquivo");
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className={cn("space-y-2", className)}>
      <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
        {label}
      </p>
      <div className="flex items-center gap-4">
        <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border border-zinc-200 bg-zinc-50">
          {value ? (
            <img src={value} alt={label} className="h-full w-full object-contain" />
          ) : (
            <ImagePlus className="h-6 w-6 text-zinc-300" />
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              e.target.value = "";
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            {value ? "Trocar logo" : "Enviar logo"}
          </Button>
          {value && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="inline-flex items-center gap-1 text-xs text-zinc-500 transition-colors hover:text-red-600"
            >
              <Trash2 className="h-3 w-3" />
              Remover
            </button>
          )}
          <p className="text-[11px] text-zinc-500">PNG/JPG max 500KB</p>
        </div>
      </div>
    </div>
  );
}
